import type { SuperHubRouteParams } from './types';
import type { PropsWithChildren, FC } from 'react';

import { useParams } from 'react-router-dom';

import { superHubSidebarTypes, useSuperHubStore } from '@core/store';
import RedirectWithHash from '@core/utils/RedirectWithHash';

import { isSidebarLoadedForSection } from './sidebar';

/**
 * Determines if a document is currently loaded. If not, attempts to redirect
 * the user to the first page or post in the sidebar based on the current
 * section we're on. If no first page is available, cancel the redirect and stay
 * where we are.
 */
const RedirectToSidebarFirstPage: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const { action, section, slug } = useParams<SuperHubRouteParams>();
  const [findSidebarFirstPage, isLoading, sidebarKey, isEditing, isSuperHubAdmin] = useSuperHubStore(s => [
    s.sidebar.findSidebarFirstPage,
    s.sidebar.isLoading,
    s.sidebar.swrKey,
    s.isEditing,
    s.user.role === 'admin',
  ]);

  // Never redirect on create routes.
  if (action === 'create') return <>{children}</>;

  // Never redirect from the API Definitions route.
  if (action === 'update' && section === 'reference' && !slug) return <>{children}</>;

  if (section && !slug && superHubSidebarTypes[section]) {
    /**
     * Indicates whether the sidebar for the current section has been loaded.
     * Only then, can we proceed to find the first page to redirect to.
     */
    const isSidebarLoaded = isSidebarLoadedForSection({ isLoading, section, sidebarKey });
    if (isSidebarLoaded) {
      const firstPage = findSidebarFirstPage({
        includeHidden: isSuperHubAdmin,
        includeLinks: isEditing,
      });

      if (firstPage) {
        const redirectPath = [action, section, firstPage.slug].filter(Boolean).join('/');
        return <RedirectWithHash to={`/${redirectPath}`} />;
      }
    } else {
      // Sidebar data isn't ready yet, so we can't determine the redirect target.
      // For view routes, keep the screen blank to avoid rendering stale content.
      // For editor routes, keep the UI shell mounted so it can show a loading state.
      if (!action) return <></>;
      return <>{children}</>;
    }
  }

  return <>{children}</>;
};

export default RedirectToSidebarFirstPage;
